import { useMemo, useCallback } from "react";
import { ExternalLink, Globe, Calendar, User, X } from "lucide-react";
import { cn } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { VIEW_TYPES } from "@/other/constants/OtherKeys";
import type { MarketingPage } from "@/bryntum-lib/models/MarketingPage";

function formatRelative(d: Date | string | null | undefined): string {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

interface LinkedPageRowProps {
  page: MarketingPage;
  creatorName: string | null;
  onNavigate: (id: number) => void;
}

const LinkedPageRow = ({ page, creatorName, onNavigate }: LinkedPageRowProps) => {
  const externalHref = page.url
    ? page.url.startsWith("http") ? page.url : `https://${page.url}`
    : null;

  return (
    <div className="group relative rounded-lg border border-border/60 bg-card transition-all hover:border-primary/30 hover:shadow-sm">
      {/* Whole-card click target */}
      <button
        type="button"
        onClick={() => onNavigate(Number(page.id))}
        className="flex w-full flex-col gap-2 p-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-lg"
      >
        {/* Title row */}
        <div className="flex items-start gap-2 pr-7">
          <span className="text-[13px] font-medium text-foreground leading-snug line-clamp-2 group-hover:text-primary transition-colors">
            {page.title || "Untitled page"}
          </span>
        </div>

        {/* Slug */}
        {page.slug && (
          <span className="font-mono text-[10px] text-muted-foreground/50 truncate leading-none bg-muted/50 rounded px-1.5 py-1 self-start max-w-full block">
            /{page.slug}
          </span>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground/50 pt-0.5">
          {creatorName && (
            <span className="inline-flex items-center gap-1 truncate">
              <User className="size-2.5 shrink-0" />
              <span className="truncate">{creatorName}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1 ml-auto shrink-0">
            <Calendar className="size-2.5" />
            {formatRelative(page.updatedAt)}
          </span>
        </div>
      </button>

      {/* External link — sits above the card button, top-right */}
      {externalHref && (
        <a
          href={externalHref}
          target="_blank"
          rel="noopener noreferrer"
          title="Open page in new tab"
          className="absolute top-2.5 right-2.5 rounded-md border border-border/50 bg-muted/60 p-1 transition-colors hover:border-primary/30 hover:bg-primary/5 focus-visible:outline-none"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="size-2.5 text-muted-foreground/50 group-hover:text-primary/60 transition-colors" />
        </a>
      )}
    </div>
  );
};

interface LinkedPagesPanelProps {
  pages: MarketingPage[];
  /** Overrides the default "Published Pages" header label. */
  headerTitle?: string;
  /** When provided, renders a close button in the header. */
  onClose?: () => void;
}

export const LinkedPagesPanel = ({ pages, headerTitle, onClose }: LinkedPagesPanelProps) => {
  const { project, viewManager } = useSwivuBryntumContext();

  const handleNavigate = useCallback((id: number) => {
    const view = viewManager?.getDefaultViewByType(VIEW_TYPES.marketingPages);
    if (view) {
      viewManager?.selectView(view);
      viewManager?.selectViewAnonymousView(view.id, view.name, view.viewType, { marketingPageId: id });
    }
  }, [viewManager]);

  const creatorNames = useMemo(() => {
    const map = new Map<number, string | null>();
    for (const page of pages) {
      if (page.creator == null) { map.set(page.id, null); continue; }
      const bu = (project as any)?.boardUsersStore?.getById(page.creator) as any;
      map.set(page.id, bu?.name ?? null);
    }
    return map;
  }, [pages, project]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-border bg-accent">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 shrink-0">
            <Globe className="size-3 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-medium text-muted-foreground/70 uppercase tracking-wider leading-none mb-0.5">
              Published Pages
            </p>
            {headerTitle && (
              <p className="text-xs font-medium text-foreground truncate leading-snug">
                {headerTitle}
              </p>
            )}
          </div>
          {pages.length > 0 && (
            <span className="shrink-0 inline-flex items-center justify-center h-5 min-w-5 rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary leading-none">
              {pages.length}
            </span>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close panel"
              className={cn(
                "shrink-0 rounded-md p-1 text-muted-foreground/50 transition-colors",
                "hover:bg-muted hover:text-foreground focus-visible:outline-none",
              )}
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-background">
        {pages.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 h-full min-h-[8rem] px-5 py-10 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-dashed border-border bg-muted/40">
              <Globe className="size-4 text-muted-foreground/40" />
            </div>
            <div className="space-y-1">
              <p className="text-xs font-medium text-foreground">No published pages</p>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Publish this work file to generate a page.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2 p-3">
            {pages.map((page) => (
              <LinkedPageRow
                key={page.id}
                page={page}
                creatorName={creatorNames.get(page.id) ?? null}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
